/**
 * One request.
 *
 * Reached when a customer needs to explain a single result: why it failed, why it was slow, or why
 * it cost a credit. The credit disposition is shown in plain words rather than as a code, because
 * "you were charged for this" is the question support actually receives.
 *
 * A 404 is the expected failure — the request does not exist, or belongs to another workspace, and
 * the API deliberately does not distinguish the two. It is presented as an ordinary "not found"
 * rather than an error state, since both cases mean the same thing to the reader.
 */
import { Link, useParams } from '@tanstack/react-router';
import { formatRendererIdentity } from '@urlshot/api-contract';
import { useRequestLog, type RequestLogDetail } from '../../lib/api';
import { Card, StatusBadge } from '../../components/ui';

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  return ms < 1000 ? `${String(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

/** Plain-language explanation of the charge, which is what support is usually asked about. */
function creditExplanation(disposition: string, credits: number): string {
  if (disposition === 'cache_hit') return 'Served from cache. No credit was used.';
  if (disposition === 'pre_browser_failure') {
    return 'Failed before the browser started, so no credit was used.';
  }
  return credits === 1
    ? 'A browser render was attempted, so one credit was used — even if the page itself failed.'
    : 'No credit was used.';
}

/**
 * How the request authenticated, in the product's own words.
 *
 * Shown because a signed URL is anonymous: anyone who has one spends this workspace's credits, and
 * this is how a customer tells that traffic from their own backend's.
 */
function authenticationLabel(mode: RequestLogDetail['authenticationMode']): string {
  if (mode === 'signed_url') return 'Signed URL';
  if (mode === 'api_key') return 'Secret key';
  if (mode === 'demo') return 'Website demo';
  // Logged before the mode was stored.
  return '—';
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-t border-neutral-200 py-2 text-sm">
      <span className="text-neutral-500">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

export function RequestLogDetailRoute() {
  // The route *id*, not the URL. The layout route is pathless (`id: 'app'`), so its id prefixes
  // the child's, while the address bar shows `/request-logs/:requestId`. Type-checked, so a router
  // change surfaces here rather than as a missing parameter at runtime.
  const { requestId } = useParams({ from: '/app/request-logs/$requestId' });
  const { data, isLoading, isError } = useRequestLog(requestId);

  if (isLoading) {
    return (
      <Card>
        <p className="text-sm text-neutral-600">Loading…</p>
      </Card>
    );
  }

  if (isError || data === undefined) {
    return (
      <Card data-testid="request-log-not-found">
        <h1 className="text-base font-semibold">Request not found</h1>
        <p className="mt-1 text-sm text-neutral-600">
          No request with this identifier exists in this workspace.
        </p>
        <Link to="/request-logs" className="link mt-4 inline-block text-sm">
          Back to request logs
        </Link>
      </Card>
    );
  }

  return (
    <>
      <div>
        <Link to="/request-logs" className="link text-sm">
          Back to request logs
        </Link>
        <h1 className="mt-2 text-xl font-semibold" data-testid="request-log-detail-id">
          {data.requestId}
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          {new Date(data.requestedAt).toLocaleString()}
        </p>
      </div>

      <Card>
        <h2 className="text-base font-semibold">Outcome</h2>
        <div className="mt-2">
          <Row label="Status" value={<StatusBadge status={data.status} />} />
          <Row label="HTTP status" value={data.httpStatus} />
          {data.errorCode !== null && <Row label="Error code" value={data.errorCode} />}
          <Row label="Target hostname" value={data.targetHostname ?? '—'} />
          <Row label="Cache" value={`${data.cacheStatus} (${data.cacheSource})`} />
          <Row label="Duration" value={formatDuration(data.durationMs)} />
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold">Credits</h2>
        <p className="mt-1 text-sm text-neutral-600" data-testid="request-log-credit-explanation">
          {creditExplanation(data.creditDisposition, data.credits)}
        </p>
        <div className="mt-2">
          <Row label="Credits used" value={data.credits} />
          <Row label="API key" value={data.keyId ?? '—'} />
          <Row
            label="Authenticated with"
            value={
              <span data-testid="request-log-authentication">
                {authenticationLabel(data.authenticationMode)}
              </span>
            }
          />
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold">Render</h2>
        <div className="mt-2">
          {/*
            The same string the customer's response carried in `X-Urlshot-Renderer`, so a support
            conversation does not need the two to be reconciled by eye. The full build is kept
            underneath it: twelve characters identify a commit, forty resolve it without a lookup.
          */}
          <Row
            label="Renderer"
            value={
              data.rendererVersion === null
                ? '—'
                : formatRendererIdentity(data.rendererVersion, data.rendererFunctionVersion)
            }
          />
          <Row label="Build" value={data.rendererVersion ?? '—'} />
          <Row
            label="Cold start"
            value={data.rendererCold === null ? '—' : data.rendererCold ? 'Yes' : 'No'}
          />
          <Row
            label="Browser started"
            value={data.browserStarted === null ? '—' : data.browserStarted ? 'Yes' : 'No'}
          />
          <Row label="Target response" value={data.targetStatus ?? '—'} />
          <Row label="Redirects" value={data.redirectCount ?? '—'} />
          <Row label="Navigation" value={formatDuration(data.timings.navigationMs)} />
          <Row label="Screenshot" value={formatDuration(data.timings.screenshotMs)} />
        </div>
      </Card>

      {data.options !== null && (
        <Card>
          <h2 className="text-base font-semibold">Options</h2>
          <p className="mt-1 text-xs text-neutral-500">
            The normalized options this request was rendered with. The target URL is not stored.
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-neutral-50 p-3 text-xs">
            {JSON.stringify(data.options, null, 2)}
          </pre>
        </Card>
      )}
    </>
  );
}
